import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const CustomerAuthContext = createContext(null);

const TOKEN_KEY = 'customer_token';
const USER_KEY = 'customer_user';

export const CustomerAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (storedToken) {
      setToken(storedToken);
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          // ignore
        }
      }
    }
    setLoading(false);
  }, []);

  const login = (accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  };

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) return null;
    try {
      const { customerAuthAPI } = await import('../lib/customerApi');
      const res = await customerAuthAPI.me();
      const u = res.data?.user;
      if (u) {
        setUser(u);
        localStorage.setItem(USER_KEY, JSON.stringify(u));
        return u;
      }
    } catch {
      // keep existing user in state
    }
    return null;
  }, []);

  const isAuthenticated = !!token;

  return (
    <CustomerAuthContext.Provider
      value={{ user, token, login, logout, refreshUser, isAuthenticated, loading }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return ctx;
};
